import React, { useRef, useState } from 'react';
import { FileDown, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { stationNetworkApi } from '../../lib/stationNetworkApi';
import { messageErreur } from '../../lib/erreurs';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { StatutBadge } from '../ui/StatutBadge';

/**
 * Reprise d'un réseau par Excel (lot 034, administrateur) : stations, cuves,
 * barèmes, pompes, pistolets. « Vérifier » rend le rapport sans rien écrire ;
 * « Reprendre » écrit les lignes valides. Le rapport dit, ligne par ligne, ce
 * qui est créé, ce qui existait déjà et ce qui est refusé (avec la raison).
 */

const CELLULES =
  'w-full border-collapse text-sm [&_td]:border-b [&_td]:border-border/60 [&_td]:px-3 [&_td]:py-2 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground';

const NetworkImport = () => {
  const champ = useRef(null);
  const [fichier, setFichier] = useState(null);
  const [rapport, setRapport] = useState(null);
  const [enCours, setEnCours] = useState('');

  const modele = async () => {
    try {
      await stationNetworkApi.importNetwork.template();
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de télécharger le modèle'));
    }
  };

  const lancer = async (verification) => {
    if (!fichier) return;
    setEnCours(verification ? 'verification' : 'reprise');
    try {
      const resultat = await stationNetworkApi.importNetwork.run(fichier, { dryRun: verification });
      setRapport(resultat);
      if (verification) {
        toast.success('Vérification terminée : rien n’a été écrit');
      } else {
        toast.success(`Reprise terminée : ${resultat.summary.created} ligne${resultat.summary.created > 1 ? 's' : ''} créée${resultat.summary.created > 1 ? 's' : ''}`);
      }
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de lire le fichier'));
    } finally {
      setEnCours('');
    }
  };

  const choisir = (event) => {
    setFichier(event.target.files?.[0] || null);
    setRapport(null);
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="outline" onClick={modele}>
          <FileDown className="h-4 w-4" aria-hidden="true" />
          Modèle Excel
        </Button>
        <input ref={champ} type="file" accept=".xlsx" className="sr-only" id="reprise-fichier" onChange={choisir} />
        <Button type="button" variant="outline" onClick={() => champ.current?.click()}>
          <Upload className="h-4 w-4" aria-hidden="true" />
          Choisir le fichier
        </Button>
        {fichier && <span className="text-sm text-muted-foreground">{fichier.name}</span>}
        <div className="ml-auto flex gap-2">
          <Button type="button" variant="outline" disabled={!fichier || Boolean(enCours)} onClick={() => lancer(true)}>
            Vérifier
          </Button>
          <Button type="button" disabled={!fichier || Boolean(enCours)} onClick={() => lancer(false)}>
            Reprendre
          </Button>
        </div>
      </div>

      {rapport && (
        <>
          <div className="flex flex-wrap gap-2">
            <Badge variant="success">{rapport.summary.created} à créer ou créées</Badge>
            <Badge variant="muted">{rapport.summary.skipped} existent déjà</Badge>
            <Badge variant={rapport.summary.error ? 'destructive' : 'muted'}>{rapport.summary.error} refusées</Badge>
            {rapport.dry_run && <Badge variant="info">Vérification</Badge>}
          </div>
          <section className="overflow-x-auto rounded-xl border border-border bg-card px-1 py-2 shadow-sm">
            <table className={CELLULES}>
              <thead>
                <tr>
                  <th className="text-left">Feuille</th>
                  <th className="text-right">Ligne</th>
                  <th className="text-left">Élément</th>
                  <th className="text-left">Résultat</th>
                  <th className="text-left">Raison</th>
                </tr>
              </thead>
              <tbody>
                {rapport.lines.map((ligne, index) => (
                  <tr key={`${ligne.sheet}-${ligne.row}-${index}`} className={ligne.status === 'error' ? 'bg-destructive/5' : undefined}>
                    <td>{ligne.sheet}</td>
                    <td className="text-right tabular-nums">{ligne.row}</td>
                    <td className="font-medium">{ligne.label || '—'}</td>
                    <td>
                      <StatutBadge domaine="station_reprise" code={ligne.status} />
                    </td>
                    <td>{ligne.message || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
};

export default NetworkImport;
